"use client";

import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useSession } from "next-auth/react";
import { sl, en, type Translations, type Language } from "./index";

const translations: Record<Language, Translations> = { sl, en };

interface I18nContextType {
  lang: Language;
  t: Translations;
  setLang: (lang: Language) => void;
  /** ISO 639-1 content language (~80 supported). Falls back to `lang`
   *  until /api/user/content-language returns the user's saved value. */
  contentLang: string;
  setContentLang: (code: string) => void;
}

const I18nContext = createContext<I18nContextType>({
  lang: "sl",
  t: sl,
  setLang: () => {},
  contentLang: "sl",
  setContentLang: () => {},
});

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function setCookie(name: string, value: string, days = 365) {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

interface I18nProviderProps {
  children: React.ReactNode;
  initialLanguage?: Language;
}

export function I18nProvider({ children, initialLanguage }: I18nProviderProps) {
  // Only logged-in users have DB-stored language prefs. On public/logged-out
  // pages we skip the protected /api/user/* calls entirely (they'd 401) and
  // rely on the SSR-resolved `initialLanguage` + cookie instead.
  const { status: sessionStatus } = useSession();
  const [lang, setLangState] = useState<Language>(() => {
    if (initialLanguage) return initialLanguage;
    const cookie = getCookie("lang");
    if (cookie === "en" || cookie === "sl") return cookie;
    // EN is the default market; the server (RootLayout) resolves the real
    // initial language (cookie → Accept-Language) and passes it as
    // `initialLanguage`, so this fallback only runs if that's ever missing.
    return "en";
  });

  useEffect(() => {
    if (initialLanguage && initialLanguage !== lang) {
      setLangState(initialLanguage);
    }
  }, [initialLanguage]);

  // Content language is separate from UI language (lang). Falls back to lang
  // until DB returns the saved value. See src/lib/i18n/languages.ts for the
  // full ISO 639-1 list of supported values.
  const [contentLang, setContentLangState] = useState<string>(() => {
    const cookie = getCookie("contentLang");
    if (cookie) return cookie;
    return lang;
  });

  // Sync language from DB on mount — DB is the source of truth (the email
  // cron reads it). Cookie + `initialLanguage` from SSR are just hints for
  // first paint. Previously this short-circuited when SSR provided a value,
  // which let cookie & DB drift apart — a user who saw SL on screen could
  // still receive EN emails (the cron reads DB). Now we always reconcile
  // and bring cookie + UI in line with DB.
  useEffect(() => {
    // Gate on an authenticated session — these routes require auth, so on a
    // logged-out public page they'd return a guaranteed 401. `useSession`
    // already polls /api/auth/session, so this adds no extra round-trip.
    if (sessionStatus !== "authenticated") return;
    fetch("/api/user/language")
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.language && (data.language === "sl" || data.language === "en") && data.language !== lang) {
          setLangState(data.language);
          setCookie("lang", data.language);
        }
      })
      .catch(() => {});
    fetch("/api/user/content-language")
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (typeof data?.contentLanguage === "string" && data.contentLanguage !== contentLang) {
          setContentLangState(data.contentLanguage);
          setCookie("contentLang", data.contentLanguage);
        }
      })
      .catch(() => {});
  }, [sessionStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLang = useCallback((newLang: Language) => {
    setLangState(newLang);
    setCookie("lang", newLang);
    // Persist to DB only when signed in — logged-out users keep their choice
    // in the cookie, and the protected route would just 401.
    if (sessionStatus === "authenticated") {
      fetch("/api/user/language", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ language: newLang }),
      }).catch(() => {});
    }
  }, [sessionStatus]);

  const setContentLang = useCallback((code: string) => {
    setContentLangState(code);
    setCookie("contentLang", code);
    if (sessionStatus === "authenticated") {
      fetch("/api/user/content-language", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      }).catch(() => {});
    }
  }, [sessionStatus]);

  return (
    <I18nContext.Provider value={{ lang, t: translations[lang], setLang, contentLang, setContentLang }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  return useContext(I18nContext);
}
